import './App.css'
import React, { Suspense, lazy, useState, useEffect, useMemo } from 'react';
import { Router, Route, Switch } from 'wouter';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import Home from './pages/Home';
import Play from './pages/Play';
import Decks from './pages/Decks';
import Stats from './pages/Stats';
import Cards from './pages/Cards';
import Meta from './pages/Meta';
import CardDebug from './pages/CardDebug';
import CardSandbox from './pages/CardSandbox';
import NotFound from './pages/NotFound';

const PlayV2ActionPreview = import.meta.env.DEV ? lazy(() => import('./pages/PlayV2ActionPreview')) : null;

function App() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardLoadError, setCardLoadError] = useState('');
  const [cardLoadAttempt, setCardLoadAttempt] = useState(0);

  // Load cards from the backend
  useEffect(() => {
    const controller = new AbortController();

    const fetchCards = async () => {
      try {
        setLoading(true);
        setCardLoadError('');
        // Using environment variable for API URL, default to relative path to avoid mixed content
        const apiUrl = import.meta.env.VITE_API_BASE_URL || '';
        const response = await fetch(`${apiUrl}/api/cards-for-deck`, {
          signal: controller.signal,
        }); // Use the endpoint that returns all cards directly
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        // Normalize API response to ALWAYS be an array
        const cardsData =
          Array.isArray(data) ? data :
          (data && Array.isArray(data.items)) ? data.items :
          (data && Array.isArray(data.cards)) ? data.cards :
          [];

        console.log('Loaded cards:', cardsData.length, 'cards');
        setCards(cardsData);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Error fetching cards:', err);
        setCardLoadError('Impossibile caricare il catalogo carte. Alcune pagine potrebbero essere incomplete.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchCards();
    return () => controller.abort();
  }, [cardLoadAttempt]);

  // Memoize cards to prevent unnecessary re-renders
  const memoizedCards = useMemo(() => (Array.isArray(cards) ? cards : []), [cards]);

  return (
    <AuthProvider>
      <Router>
        <Layout>
          {cardLoadError ? (
            <div
              role="alert"
              data-testid="global-card-load-error"
              className="mx-auto mt-3 flex w-[min(100%-1rem,72rem)] items-center justify-between gap-4 rounded-md border border-red-700/60 bg-red-950/80 px-4 py-3 text-sm text-red-100"
            >
              <span>{cardLoadError}</span>
              <button
                type="button"
                className="shrink-0 rounded border border-red-400/50 px-3 py-1 font-semibold hover:bg-red-900"
                onClick={() => setCardLoadAttempt((attempt) => attempt + 1)}
                disabled={loading}
              >
                Riprova
              </button>
            </div>
          ) : null}
          <Switch>
            <Route path="/">
              {() => <Home cards={memoizedCards} />}
            </Route>
            <Route path="/play">
              {() => <Play cards={memoizedCards} />}
            </Route>
            <Route path="/play-v2">
              {() => <Play cards={memoizedCards} />}
            </Route>
            <Route path="/decks">
              {() => <Decks cards={memoizedCards} />}
            </Route>
            <Route path="/cards">
              {() => <Cards cards={memoizedCards} />}
            </Route>
            <Route path="/stats">
              {() => <Stats />}
            </Route>
            <Route path="/meta">
              {() => <Meta />}
            </Route>
            <Route path="/card-debug">
              {() => <CardDebug />}
            </Route>
            <Route path="/card-sandbox">
              {() => <CardSandbox />}
            </Route>
            {PlayV2ActionPreview && (
              <Route path="/play-v2-action-preview">
                {() => (
                  <Suspense fallback={null}>
                    <PlayV2ActionPreview />
                  </Suspense>
                )}
              </Route>
            )}
            <Route>
              {() => <NotFound />}
            </Route>
          </Switch>
        </Layout>
      </Router>
    </AuthProvider>
  );
}

export default App
